import { tv } from 'tailwind-variants';

export const styleButton = tv({
  slots: {
    container: [
      'w-full flex items-center gap-2 justify-center',
      'transition-all active:animate-pulse-click',
      'rounded-lg font-semibold text-base',
    ],
    icon: 'text-lg',
  },

  variants: {
    variant: {
      default: {
        container: [
          'inline-flex justify-center items-center',
          'transition ease-in-out duration-150 group',
          'outline-none focus:outline-none focus-visible:outline-none',
          'focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2',
          'font-semibold rounded-lg cursor-pointer',
          'py-3 px-4 whitespace-nowrap shadow-tooltip text-lg',
        ],
      },
      secondary: {
        container: [
          'bg-green-100 text-green-500',
          'hover:bg-green-200',
          'py-3 px-4',
          'whitespace-nowrap',
          'active:text-green-200 text-lg',
        ],
      },
      outline: {
        container: [
          'bg-transparent border border-gray-300',
          'hover:bg-gray-100',
          'py-3 px-4',
          'whitespace-nowrap',
          'active:bg-gray-200 text-lg',
        ],
      },
      text: {
        container: [
          'bg-transparent inline-flex justify-center items-center transition ease-in-out',
          'duration-150 whitespace-nowrap',
          'group outline-none focus:outline-none focus-visible:outline-none',
          'focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2',
          'hover:underline font-semibold rounded-lg cursor-pointer',
        ],
      },
    },
    color: {
      default: {},
      primary: {},
      warning: {},
      danger: {},
      success: {},
    },
    asIcon: {
      true: {
        container: 'w-12 min-w-[3rem] h-12',
      },
    },
    disabled: {
      true: {
        container: ['text-gray-400 !bg-gray-100 pointer-events-none disabled', 'animate-none', 'shadow-transparent'],
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    color: 'default',
    disabled: false,
  },
  compoundVariants: [
    {
      variant: 'default',
      color: 'default',
      disabled: false,
      className: {
        container: ['bg-gray-500 hover:bg-gray-600 active:bg-gray-700', 'text-white active:text-gray-900'],
      },
    },
    {
      variant: 'default',
      color: 'primary',
      disabled: false,
      className: {
        container: ['bg-green-500 hover:bg-green-600 active:bg-green-900', 'text-white'],
      },
    },
    {
      variant: 'default',
      color: 'warning',
      disabled: false,
      className: {
        container: ['bg-warning-500 hover:bg-warning-600 active:bg-warning-700', 'text-white'],
      },
    },
    {
      variant: 'default',
      color: 'danger',
      disabled: false,
      className: {
        container: ['bg-danger-600 hover:bg-danger-700 active:bg-danger-900', 'text-white'],
      },
    },
    {
      variant: 'default',
      color: 'success',
      disabled: false,
      className: {
        container: ['bg-success-300 hover:bg-success-400 active:bg-success-500', 'text-success-800 hover:text-success-900'],
      },
    },
    {
      variant: 'secondary',
      color: 'default',
      disabled: false,
      className: {
        container: [
          'bg-gray-200 hover:bg-gray-300 active:bg-gray-300',
          'text-gray-500',
          'hover:text-gray-600 active:text-gray-800',
        ],
      },
    },
    {
      variant: 'secondary',
      color: 'primary',
      disabled: false,
      className: {
        container: [
          'bg-green-100 hover:bg-green-200 active:bg-green-300',
          'text-green-500 hover:text-green-600 active:text-green-900',
        ],
      },
    },
    {
      variant: 'secondary',
      color: 'warning',
      disabled: false,
      className: {
        container: [
          'bg-warning-100 hover:bg-warning-200 active:bg-warning-300',
          'text-warning-600 hover:text-warning-700 active:text-warning-900',
        ],
      },
    },
    {
      variant: 'secondary',
      color: 'danger',
      disabled: false,
      className: {
        container: [
          'bg-danger-100 hover:bg-danger-200 active:bg-danger-300',
          'text-danger-600 hover:text-danger-700 active:text-danger-900',
        ],
      },
    },
    {
      variant: 'outline',
      color: 'default',
      disabled: false,
      className: {
        container: [
          'bg-transparent border-gray-300 hover:bg-gray-100 active:bg-gray-200',
          'text-gray-600 hover:text-gray-700 active:text-gray-900',
        ],
      },
    },
    {
      variant: 'outline',
      color: 'primary',
      disabled: false,
      className: {
        container: [
          'bg-transparent border-green-500 hover:bg-green-50 active:bg-green-200',
          'text-green-500 hover:text-green-700 active:text-green-900',
        ],
      },
    },
    {
      variant: 'outline',
      color: 'warning',
      disabled: false,
      className: {
        container: [
          'bg-transparent border-warning-500 hover:bg-warning-100 active:bg-warning-200',
          'text-warning-500 hover:text-warning-600 active:text-warning-700',
        ],
      },
    },
    {
      variant: 'outline',
      color: 'danger',
      disabled: false,
      className: {
        container: [
          'bg-transparent border-danger-500 hover:bg-danger-100 active:bg-danger-200',
          'text-danger-500 hover:text-danger-600 active:text-danger-700',
        ],
      },
    },
    {
      variant: 'text',
      color: 'default',
      className: {
        container: ['text-gray-600 hover:text-gray-700 active:text-gray-900'],
      },
    },
    {
      variant: 'text',
      color: 'primary',
      className: {
        container: ['text-green-500 hover:text-green-600 active:text-green-700'],
      },
    },
    {
      variant: 'text',
      color: 'warning',
      className: {
        container: ['text-warning-500 hover:text-warning-600 active:text-warning-600'],
      },
    },
    {
      variant: 'text',
      color: 'danger',
      className: {
        container: ['text-danger-600 hover:text-danger-700 active:text-danger-900'],
      },
    },
    {
      variant: 'text',
      disabled: true,
      className: {
        container: ['bg-transparent text-gray-400'],
      },
    },
  ],
});
